// custom-stemregenHorizon: Custom quick-add functionality for manually placed buttons in product cards
import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { CartAddEvent } from '@theme/events';

/**
 * Custom StemRegen Quick Add Component
 * Extends the existing Component to work with custom buttons placed via the theme customizer
 */
export class CustomStemRegenQuickAdd extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Function | null} */
  #variantUpdateHandler = null;

  /** @type {Function | null} */
  #skioUpdateHandler = null;

  /** @type {Function | null} */
  #cartAddHandler = null;

  connectedCallback() {
    super.connectedCallback();
    console.log('CustomStemRegenQuickAdd connected to DOM');

    // Find the modal trigger button within this component
    this.modalTrigger = this.querySelector('[data-quick-add-trigger]');
    console.log('Modal trigger button found:', !!this.modalTrigger);

    if (this.modalTrigger) {
      this.modalTrigger.addEventListener('click', this.handleModalTrigger.bind(this));
      console.log('Click listener added to modal trigger button');
    }

    // Setup sync with main product form if enabled
    if (this.dataset.syncWithPage === 'true') {
      this.setupPageSync();
    }
  }

  /**
   * Setup synchronization with the main product form on the page
   * This syncs variant ID and selling plan (subscription) selection
   */
  setupPageSync() {
    // Find the main product form (not ours)
    const mainProductForm = document.querySelector('product-form-component form[data-type="add-to-cart-form"]');
    if (!mainProductForm || this.contains(mainProductForm)) {
      // No main form found, or it's our own form - skip sync
      console.log('CustomStemRegenQuickAdd: No main product form found for sync');
      return;
    }

    console.log('CustomStemRegenQuickAdd: Setting up page sync');

    // Get our form
    const ourForm = this.querySelector('form[data-type="add-to-cart-form"]');
    if (!ourForm) {
      console.log('CustomStemRegenQuickAdd: No form found in this component');
      return;
    }

    // Do initial sync
    this.syncFromMainForm(mainProductForm, ourForm);

    // Listen for variant updates
    this.#variantUpdateHandler = (event) => {
      console.log('CustomStemRegenQuickAdd: variant:update event received');
      this.syncFromMainForm(mainProductForm, ourForm);
    };
    document.addEventListener('variant:update', this.#variantUpdateHandler);

    // Listen for SKIO selling plan updates
    this.#skioUpdateHandler = (event) => {
      console.log('CustomStemRegenQuickAdd: skio::update-selling-plan event received', event.detail);
      // Small delay to let SKIO update the DOM
      setTimeout(() => this.syncFromMainForm(mainProductForm, ourForm), 150);
    };
    document.addEventListener('skio::update-selling-plan', this.#skioUpdateHandler);
  }

  /**
   * Sync form values from main product form to our form
   * @param {HTMLFormElement} mainForm - The main product form
   * @param {HTMLFormElement} ourForm - Our quick add form
   */
  syncFromMainForm(mainForm, ourForm) {
    // Sync variant ID
    const mainVariantInput = mainForm.querySelector('input[name="id"]');
    const ourVariantInput = ourForm.querySelector('input[name="id"]');
    if (mainVariantInput && ourVariantInput) {
      ourVariantInput.value = mainVariantInput.value;
      console.log('CustomStemRegenQuickAdd: Synced variant ID:', mainVariantInput.value);
    }

    // Sync selling plan
    const ourSellingPlanInput = ourForm.querySelector('input[name="selling_plan"]');
    if (ourSellingPlanInput) {
      // Try to find selling_plan input in main form
      let mainSellingPlanInput = mainForm.querySelector('input[name="selling_plan"]');

      // If not in form, check for form attribute association
      if (!mainSellingPlanInput) {
        const formId = mainForm.getAttribute('id');
        if (formId) {
          mainSellingPlanInput = document.querySelector(`input[name="selling_plan"][form="${formId}"]`);
        }
      }

      // Check all selling_plan inputs on page, excluding our own
      if (!mainSellingPlanInput) {
        const allSellingPlanInputs = document.querySelectorAll('input[name="selling_plan"]');
        for (const input of allSellingPlanInputs) {
          if (ourForm.contains(input)) continue;
          mainSellingPlanInput = input;
          break;
        }
      }

      if (mainSellingPlanInput) {
        ourSellingPlanInput.value = mainSellingPlanInput.value || '';
        console.log('CustomStemRegenQuickAdd: Synced selling plan:', mainSellingPlanInput.value);
      }
    }
  }
  
  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.modalTrigger) {
      this.modalTrigger.removeEventListener('click', this.handleModalTrigger.bind(this));
    }

    // Clean up sync event listeners
    if (this.#variantUpdateHandler) {
      document.removeEventListener('variant:update', this.#variantUpdateHandler);
      this.#variantUpdateHandler = null;
    }
    if (this.#skioUpdateHandler) {
      document.removeEventListener('skio::update-selling-plan', this.#skioUpdateHandler);
      this.#skioUpdateHandler = null;
    }
    if (this.#cartAddHandler) {
      document.removeEventListener(CartAddEvent.eventName, this.#cartAddHandler);
      this.#cartAddHandler = null;
    }
  }
  
  /**
   * Handle click on the modal trigger button
   * Uses the existing quick-add dialog to display the product
   * @param {Event} event - The click event
   */
  async handleModalTrigger(event) {
    event.preventDefault();
    console.log('Custom quick-add button clicked');
    
    // Get product data from attributes
    const productHandle = this.dataset.productHandle;
    const variantId = this.dataset.variantId;
    
    console.log('Product handle:', productHandle, 'Variant ID:', variantId);
    
    // Check if on mobile device (max-width: 767px for mobile breakpoint)
    const isMobile = window.matchMedia('(max-width: 767px)').matches;

    // Check if quick-add modal is available (disabled when cart drawer is active)
    const hasQuickAddModal = !!document.getElementById('quick-add-dialog');

    // On mobile, or when modal is unavailable (cart drawer mode), redirect to product page
    if (isMobile || !hasQuickAddModal) {
      console.log('Redirecting to product page -', isMobile ? 'mobile device' : 'cart drawer mode');
      const productUrl = `/products/${productHandle}`;
      window.location.href = productUrl;
      return;
    }
    
    // Find the product card to get the selected variant if available
    const productCard = this.closest('product-card');
    let selectedVariantId = variantId;
    
    // Check if product card has the getSelectedVariantId method
    if (productCard) {
      // @ts-ignore - getSelectedVariantId is a custom method on the product-card element
      const getMethod = productCard.getSelectedVariantId;
      if (typeof getMethod === 'function') {
        // @ts-ignore
        selectedVariantId = productCard.getSelectedVariantId() || variantId;
      }
    }
    
    // Build the product URL with variant parameter
    const productUrl = new URL(`/products/${productHandle}`, window.location.origin);
    if (selectedVariantId) {
      productUrl.searchParams.set('variant', selectedVariantId);
    }
    
    console.log('Fetching product URL:', productUrl.toString());
    
    // Find the existing quick-add dialog element
    const quickAddDialog = /** @type {HTMLElement | null} */ (document.getElementById('quick-add-dialog'));
    if (!quickAddDialog) {
      console.error('Quick add dialog not found');
      return;
    }
    
    console.log('Quick add dialog found:', quickAddDialog);
    
    // Abort any previous fetch requests
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    
    try {
      // Fetch the product page
      const response = await fetch(productUrl.toString(), {
        signal: this.#abortController.signal
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }
      
      console.log('Product page fetched successfully');
      
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      
      // Find the product grid element from the fetched page - using the same selector as QuickAddComponent
      const productGrid = html.querySelector('[data-product-grid-content]');
      
      console.log('Product grid found:', !!productGrid);
      
      if (productGrid) {
        // Clone the product grid to avoid modifying the original
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        
        // Update the modal content using the same method as QuickAddComponent
        const modalContent = document.getElementById('quick-add-modal-content');
        console.log('Modal content element found:', !!modalContent);
        
        if (modalContent) {
          // Morph the content into the modal
          morph(modalContent, freshContent);
          console.log('Content morphed into modal');
          
          // Open the dialog using the custom element's method if available
          // @ts-ignore - showDialog is a custom method on the quick-add-dialog element
          if (quickAddDialog.showDialog && typeof quickAddDialog.showDialog === 'function') {
            console.log('Opening dialog with showDialog method');
            // @ts-ignore
            quickAddDialog.showDialog();
          // @ts-ignore - open is a custom method on some dialog elements
          } else if (quickAddDialog.open && typeof quickAddDialog.open === 'function') {
            console.log('Opening dialog with open method');
            // @ts-ignore
            quickAddDialog.open();
          } else {
            console.log('Opening dialog with native dialog methods');
            // Use the dialog element's native showModal method
            if (quickAddDialog.tagName === 'DIALOG' && 'showModal' in quickAddDialog) {
              /** @type {HTMLDialogElement} */ (quickAddDialog).showModal();
            } else {
              quickAddDialog.setAttribute('open', '');
            }
          }

          // Auto-close modal when item is added to cart (so cart drawer can show)
          if (this.#cartAddHandler) {
            document.removeEventListener(CartAddEvent.eventName, this.#cartAddHandler);
          }
          this.#cartAddHandler = () => {
            console.log('Cart add event received - closing quick-add modal');
            // @ts-ignore
            if (quickAddDialog.closeDialog && typeof quickAddDialog.closeDialog === 'function') {
              // @ts-ignore
              quickAddDialog.closeDialog();
            } else if (quickAddDialog.close && typeof quickAddDialog.close === 'function') {
              // @ts-ignore
              quickAddDialog.close();
            }
            // Clean up listener
            document.removeEventListener(CartAddEvent.eventName, this.#cartAddHandler);
            this.#cartAddHandler = null;
          };
          document.addEventListener(CartAddEvent.eventName, this.#cartAddHandler);
        } else {
          console.error('Modal content element not found');
        }
      } else {
        console.error('Product grid not found in fetched page with [data-product-grid-content]');
        // Try alternative selectors
        const alternativeSelectors = [
          '.shopify-section .product',
          '.product',
          '[data-section-type="product"]',
          '.product-section',
          'main .product',
          '#MainContent .product'
        ];
        
        let foundElement = null;
        for (const selector of alternativeSelectors) {
          const element = html.querySelector(selector);
          if (element) {
            console.log(`Found product content with selector: ${selector}`);
            foundElement = element;
            break;
          }
        }
        
        // If we found an element with alternative selector, try to use it
        const modalContent = document.getElementById('quick-add-modal-content');
        if (foundElement && modalContent) {
          const freshContent = /** @type {Element} */ (foundElement.cloneNode(true));
          morph(modalContent, freshContent);
          
          // @ts-ignore
          if (quickAddDialog.showDialog && typeof quickAddDialog.showDialog === 'function') {
            // @ts-ignore
            quickAddDialog.showDialog();
          }
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error opening quick add modal:', error);
      }
    } finally {
      this.#abortController = null;
    }
  }
}

// Register the custom element
console.log('Registering custom-stemregen-quick-add element');
if (!customElements.get('custom-stemregen-quick-add')) {
  customElements.define('custom-stemregen-quick-add', CustomStemRegenQuickAdd);
  console.log('custom-stemregen-quick-add element registered');
} else {
  console.log('custom-stemregen-quick-add element already registered');
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCustomQuickAdd);
} else {
  initializeCustomQuickAdd();
}

function initializeCustomQuickAdd() {
  console.log('Initializing custom quick-add elements');
  // Auto-initialize any existing custom quick-add elements
  const elements = document.querySelectorAll('custom-stemregen-quick-add:not([data-initialized])');
  console.log(`Found ${elements.length} custom-stemregen-quick-add elements to initialize`);
  elements.forEach(element => {
    element.setAttribute('data-initialized', 'true');
  });
}